import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useUserCRUD } from 'src/hooks/api'
import { LoginResponse, User, UserIsChild } from 'src/types'
import provinces from 'src/data/dbProvinces.json'
import MultiSelect from 'src/components/MultiSelect/MultiSelect'
import { useSelector } from 'react-redux'
import ToggleSection from '../Asset/Form/ToggleSection'

interface UserFormProps {
  user: UserIsChild
  setUser: (value: User) => void
  hasChanges?: boolean
  onSave?: () => void
  isNew: boolean
}

const UserForm = ({ user, setUser, hasChanges, onSave, isNew }: UserFormProps) => {
  const handleChange = e => setUser({ ...user, [e.target.name]: e.target.value })
  const { role } = useSelector((s: {user: LoginResponse}) => s.user)
  const userCRUD = useUserCRUD()
  const history = useHistory()

  const [state, setState] = useState('success')

  const handleSubmit = async e => {
    e.preventDefault()
    setState('loading')
    try {
      if (user.userId) {
        await userCRUD.update(user.userId, user)
      } else {
        const res = await userCRUD.create(user)
        history.replace('/user/' + res.userId)
      }
      setState('success')
      onSave && onSave()
    } catch (e) {
      if (e?.payload?.error === 'User already exists') {
        setState('error-already')
        console.warn(e?.payload)
      } else {
        setState('error')
        console.warn(e)
      }
    }
  }

  const handleRemove = async () => {
    const msg = '¿Desea eliminar el usuario y todos sus permisos?'
    if (window.confirm(msg)) {
      await userCRUD.delete(user.userId)
      history.replace('/')
    }
  }

  const isClient = role === 'CLIENT'
  const isSemiAdmin = role === 'SEMIADMIN'

  const { geoLock: geolock, heritage } = user

  const toggleGeolock = () => {
    if (isClient) setUser({ ...user, geoLock: !user.geoLock })
  }

  const handleHeritage = () => {
    if (!isClient && !isSemiAdmin) { return }
    setUser({ ...user, heritage: !heritage })
  }

  const toggleTurnUp = (turnUp: any) => setUser({ ...user, turnUp: turnUp })

  const enabledProvinces = user.provinces
  const setEnabledProvinces = list => setUser({ ...user, provinces: list })
  const showHeritage = (isNew && isSemiAdmin) || (!isNew && user.child)

  return (
    <form id="user-form" onSubmit={handleSubmit}>
      <button className="filled" disabled={!hasChanges || state === 'loading'}>
        <FormattedMessage id={state === 'loading' ? 'saving' : hasChanges ? 'save' : 'saved'} />
      </button>
      {user.userId &&
        <span className="remove-button" onClick={handleRemove}>
          <FormattedMessage id="remove" />
        </span>
      }
      {state === 'error' &&
        <span className="error-message">
          <FormattedMessage id="save-error" />
        </span>
      }
      {state === 'error-already' &&
        <span className="error-message">
          <FormattedMessage id="save-error-already" />
        </span>
      }
      <h2 className="label-title"><FormattedMessage id="user-form.turnUp" /></h2>
      <div className="row">
        {['SI', 'NO'].map(turnUp => (
          <label key={turnUp}>
            <input
              type="radio"
              name="turnUp"
              value={turnUp}
              checked={user?.turnUp === turnUp}
              onChange={e => toggleTurnUp(e.target.value)}
            />
            <span><FormattedMessage id={`user-form.turnUp-${turnUp}`} /></span>
          </label>
        ))}
      </div>
      <label>
        <h2><FormattedMessage id="user-form.userMail" /></h2>
        <input
          name="mail"
          type="email"
          value={user.mail}
          onChange={handleChange}
          readOnly={!!user.userId}
          required={!user.userId}
        />
      </label>
      <label>
        <h2><FormattedMessage id="user-form.userName" /></h2>
        <input value={user.name} name="name" onChange={handleChange} />
      </label>
      <label>
        <h2><FormattedMessage id="user-form.userPhone" /></h2>
        <input value={user.phone} name="phone" onChange={handleChange} />
      </label>
      <label className="province-wrap">
        <h2><FormattedMessage id="user-form.province" /></h2>
        <MultiSelect
          options={provinces.map(p => ({ value: p, label: capitalize(p) }))}
          value={enabledProvinces}
          onChangeList={setEnabledProvinces}
        />
      </label>
      <label>
        <h2><FormattedMessage id="user-form.contractor" /></h2>
        <input value={user.contractor ?? ''} name="contractor" onChange={handleChange} />
      </label>
      <section>
        <h2><FormattedMessage id="user-form.armed-info.title" /></h2>
        <ToggleSection
          toggle={{
            disabled: !isClient,
            handleChange: toggleGeolock,
            value: geolock || false
          }}
          options={{
            disabled: {
              handleClick: toggleGeolock,
              title: 'switcher.not-allow'
            },
            enabled: {
              handleClick: toggleGeolock,
              title: 'switcher.allow'
            }
          }}
        />
        { showHeritage &&
          <>
            <h2><FormattedMessage id="user-form.heritage.title" /></h2>
            <ToggleSection
              toggle={{
                disabled: !isClient && !isSemiAdmin,
                handleChange: handleHeritage,
                value: heritage
              }}
              options={{
                disabled: {
                  handleClick: handleHeritage,
                  title: 'user-form.heritage.not-allow'
                },
                enabled: {
                  handleClick: handleHeritage,
                  title: 'user-form.heritage.allow'
                }
              }}
            />
          </>
        }
      </section>
    </form>
  )
}

const capitalize = (text: string): string => text
  .split(' ')
  .map(t => t[0].toUpperCase() + t.substring(1).toLowerCase())
  .join(' ')

export default UserForm
